import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";

import { vestResolver } from "@hookform/resolvers/vest";
import { format } from "date-fns";
import { useErrors } from "hooks";
import { merge } from "lodash";

import { useAppSelector } from "app/hooks";
import ErrorList from "components/error-list";
import ManagedCarePlanField from "components/managed-care-plan-field";
import ModalForm from "components/modal-form";
import PhysicianField from "components/physician-field";
import ReferralSourceFieldCombo from "components/referral-source-field-combo";
import SwitchFormField from "components/switch-form-field";
import {
  BAHAMAS_COUNTRY_VALUE,
  CANADA_COUNTRY_VALUE,
  CANADA_PROVINCES,
  COUNTRIES,
  US_COUNTRY_VALUE,
  US_STATES,
  US_VIRGIN_ISLANDS_COUNTRY_VALUE,
  US_VIRGIN_ISLANDS_TERRITORIES
} from "constants/addresses";
import {
  COMMUNICATION_METHOD_TEXT_SMS,
  COMMUNICATION_METHOD_VOICE_CALL,
  MANAGED_CARE_PAYMENT_SOURCE,
  NOAH_ES,
  PHONE_TYPE_MOBILE,
  PRIVATE_PAY_PAYMENT_SOURCE
} from "constants/patients";
import {
  patientSettingsSelector,
  selectCurrentClinic,
  selectCurrentPractice
} from "features/auth/auth-slice";
import PatientTagsField from "features/patients/patient-tags-field.component";
import {
  WizardSelectField,
  WizardTextField
} from "features/patients/new-patient/new-patient-wizard/fields";
import ClinicField from "features/scheduling/appointment-form/fields/clinic-field.component";
import { TagTypes } from "constants/tags";
import { useAddAssignedTagMutation } from "services/assigned-tags";
import { useCreateNoahPatientMutation } from "services/noah";
import { useAddPatientMutation } from "services/patients";
import { Clinic } from "services/types/clinics.type";
import { ManagedCarePlan } from "services/types/managed-care-plans.type";
import { GenderType, SexType } from "services/types/patients.type";
import { Physician } from "services/types/physician.type";
import {
  ReferralSource,
  SubReferralSource
} from "services/types/referral-source.type";
import { Tag } from "services/types/tags.type";

import DuplicatedPatientWarning from "../duplicated-patient-warning.component";
import {
  BirthdateField,
  CommunicationMethodField,
  EmailField,
  FirstNameField,
  GenderField,
  LastNameField,
  MiddleNameField,
  PaymentSourceType,
  PhoneField,
  PhoneTypeField,
  SexField
} from "./fields";
import PrimaryContactNotesField from "./fields/primary-contact-notes-field.component";
import { patientFormValidator } from "./patient-form-validator.utils";

export type WizardNextAction = "close" | "note" | "task";

type PatientFormProps = {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (patient: any, nextAction?: WizardNextAction) => void;
  newPatient: Partial<PatientFormValues>;
  wizardMode?: boolean;
};

const WIZARD_STEPS = ["Patient Details", "Note", "Task"];

export type PatientFormValues = {
  first_name: string;
  middle_name: string;
  last_name: string;
  birthdate: string | Date | null;
  birthdate_not_provided: boolean;
  sex: SexType | "";
  gender: GenderType | "";
  gender_other?: string | null;
  phone_type: string | number | Date;
  preferred_communication_method: string | number;
  payment_source_type: number | "";
  phone: string;
  email?: string | null;
  managed_care_plan: ManagedCarePlan | null;
  primary_contact_notes: string;
  referral_source?: ReferralSource | "";
  sub_referral_source?: SubReferralSource | "";
  sub_referral_source_object?: any;
  allow_create?: boolean;
  connect_to_noah?: boolean;
  preferred_clinic?: Clinic | null;

  state: string;
  street_address_1: string;
  street_address_2: string;
  zip_code: string;
  city: string;
  country: string;

  referring_physician?: Physician;
  sub_referral_source_required?: boolean;
};

const DEFAULT_VALUES: PatientFormValues = {
  first_name: "",
  middle_name: "",
  last_name: "",
  birthdate: null,
  birthdate_not_provided: false,
  sex: "",
  gender: "",
  gender_other: null,
  preferred_communication_method: COMMUNICATION_METHOD_TEXT_SMS,
  phone_type: PHONE_TYPE_MOBILE,
  payment_source_type: PRIVATE_PAY_PAYMENT_SOURCE,
  phone: "",
  email: "",
  managed_care_plan: null,
  primary_contact_notes: "",
  referral_source: "",
  sub_referral_source: "",
  sub_referral_source_object: null,
  allow_create: true,
  connect_to_noah: true,
  preferred_clinic: null,

  state: "",
  street_address_1: "",
  street_address_2: "",
  zip_code: "",
  city: "",
  country: US_COUNTRY_VALUE
};

export default function PatientForm({
  isOpen,
  onCancel,
  onSave,
  newPatient,
  wizardMode = false
}: PatientFormProps) {
  const currentPractice = useAppSelector(selectCurrentPractice);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { errors, handleResponseErrors } = useErrors({
    fieldNames: ["birthdate"],
    nonFieldNames: []
  });
  const [addPatient] = useAddPatientMutation();
  const [createNoahPatient] = useCreateNoahPatientMutation();
  const [addAssignedTag] = useAddAssignedTagMutation();
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const nextActionRef = useRef<WizardNextAction>("close");
  const patientSettings = useAppSelector(patientSettingsSelector);
  const isReferralSourceRequired =
    patientSettings?.required_fields?.referral_source;
  const isSubReferralSourceRequired =
    isReferralSourceRequired &&
    patientSettings?.required_fields?.sub_referral_source;
  const isSexRequired = patientSettings?.required_fields?.sex;
  const currentClinic = useAppSelector(selectCurrentClinic);
  const hasNoah = currentClinic?.noah_provider === NOAH_ES;
  const isCurrentClinicLoaded = Boolean(currentClinic?.id);

  const defaultValues = useMemo(
    () =>
      merge({}, DEFAULT_VALUES, newPatient, {
        connect_to_noah:
          hasNoah && Boolean(currentPractice?.auto_create_patient_in_noah),
        preferred_communication_method: currentPractice?.is_messaging_enabled
          ? COMMUNICATION_METHOD_TEXT_SMS
          : COMMUNICATION_METHOD_VOICE_CALL,
        state: currentClinic?.state || "",
        preferred_clinic: currentClinic,
        country: currentClinic?.country || US_COUNTRY_VALUE,
        payment_source_type: Number(
          currentPractice?.billing_default_payment_source_type ||
            PRIVATE_PAY_PAYMENT_SOURCE
        ),
        sub_referral_source_required: false
      }),
    [newPatient, hasNoah, currentPractice, currentClinic]
  );
  const formMethods = useForm<PatientFormValues>({
    mode: "onChange",
    resolver: vestResolver(
      patientFormValidator(
        isReferralSourceRequired,
        isSexRequired,
        isSubReferralSourceRequired
      ),
      undefined,
      {
        mode: "all"
      }
    ),
    defaultValues
  });

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    trigger,
    clearErrors
  } = formMethods;

  useEffect(() => {
    reset(defaultValues);
    setSelectedTags([]);
  }, [defaultValues, reset]);

  const [paymentSourceType, allowCreate, country] = watch([
    "payment_source_type",
    "allow_create",
    "country"
  ]);

  const gender = watch("gender");

  useEffect(() => {
    if (paymentSourceType !== String(MANAGED_CARE_PAYMENT_SOURCE)) {
      clearErrors("managed_care_plan");
    }
  }, [clearErrors, paymentSourceType]);

  useEffect(() => {
    if (gender !== "NB") {
      setValue("gender_other", null, {
        shouldDirty: true,
        shouldValidate: true
      });
    }
  }, [gender, setValue]);

  const submitPatient = useCallback(
    async (pData: PatientFormValues, nextAction?: WizardNextAction) => {
      setIsSubmitting(true);
      const {
        birthdate,
        birthdate_not_provided,
        phone,
        payment_source_type,
        phone_type,
        managed_care_plan,
        email,
        referral_source,
        sub_referral_source,
        sub_referral_source_object,
        preferred_clinic,
        allow_create, // eslint-disable-line @typescript-eslint/no-unused-vars
        connect_to_noah,
        street_address_1,
        street_address_2,
        zip_code,
        city,
        state,
        country,
        referring_physician,
        gender_other,
        sub_referral_source_required, // eslint-disable-line @typescript-eslint/no-unused-vars
        ...patientDate
      } = pData;

      const patient = await addPatient({
        ...patientDate,
        phone_type: phone_type ? String(phone_type) : undefined,
        payment_source_type: payment_source_type
          ? Number(payment_source_type)
          : null,
        preferred_clinic: preferred_clinic?.id || currentClinic?.id,
        phone: phone.replaceAll("-", ""),
        birthdate:
          birthdate && !birthdate_not_provided
            ? format(new Date(birthdate ?? ""), "yyyy-MM-dd")
            : null,
        managed_care_plan:
          Number(paymentSourceType) === MANAGED_CARE_PAYMENT_SOURCE
            ? managed_care_plan?.id
            : null,
        email: email ? email : "",
        gender_other: gender_other || null,
        sub_referral_source_content_type: sub_referral_source_object
          ? sub_referral_source_object?.content_type
          : undefined,
        sub_referral_source_object_id: sub_referral_source_object
          ? sub_referral_source_object?.id
          : undefined,
        referral_source: referral_source || null,
        sub_referral_source: sub_referral_source || null,
        street_address_1: street_address_1.trim(),
        street_address_2: street_address_2.trim(),
        city: city.trim(),
        state: country !== BAHAMAS_COUNTRY_VALUE ? state : "",
        zip_code: zip_code.trim(),
        country: country,
        referring_physician: referring_physician?.id
      });

      handleResponseErrors(patient);
      if ("data" in patient) {
        if (selectedTags.length > 0) {
          await Promise.all(
            selectedTags.map((tag) =>
              addAssignedTag({
                tag: tag.id,
                tag_type: TagTypes.PATIENT,
                object_id: patient.data.id
              })
            )
          );
        }
        if (connect_to_noah) {
          createNoahPatient(patient.data.id);
        }
        onSave(patient.data, nextAction);
        reset();
        setSelectedTags([]);
      }
      setIsSubmitting(false);
    },
    [
      addPatient,
      addAssignedTag,
      currentClinic,
      paymentSourceType,
      handleResponseErrors,
      createNoahPatient,
      onSave,
      reset,
      selectedTags
    ]
  );

  const handleWizardAction = useCallback(
    (action: WizardNextAction) => {
      nextActionRef.current = action;
      handleSubmit((pData) => submitPatient(pData, action))();
    },
    [handleSubmit, submitPatient]
  );

  const isFormDisabled = !isCurrentClinicLoaded || allowCreate === false;

  return (
    <Grid container>
      <ModalForm
        isOpen={isOpen}
        isSubmitting={isSubmitting}
        submitIsDisabled={isFormDisabled}
        onCancel={() => {
          reset();
          setSelectedTags([]);
          onCancel();
        }}
        onSubmit={
          wizardMode
            ? undefined
            : handleSubmit((pData) => submitPatient(pData))
        }
        title="New Patient Quick Add"
        hideBackdrop={false}
        formName="patient"
        control={control}
        maxWidth="lg"
        dialogActionsSx={{
          position: "sticky",
          bottom: 0
        }}
        actionsChildren={
          wizardMode ? (
            <Stack direction="row" spacing={1} sx={{ width: "100%", justifyContent: "flex-end" }}>
              <LoadingButton
                variant="outlined"
                loading={isSubmitting}
                disabled={isFormDisabled}
                onClick={() => handleWizardAction("close")}
              >
                Save & Close
              </LoadingButton>
              <LoadingButton
                variant="contained"
                loading={isSubmitting}
                disabled={isFormDisabled}
                onClick={() => handleWizardAction("note")}
              >
                Add Note
              </LoadingButton>
              <LoadingButton
                variant="contained"
                loading={isSubmitting}
                disabled={isFormDisabled}
                onClick={() => handleWizardAction("task")}
              >
                Add Task
              </LoadingButton>
            </Stack>
          ) : undefined
        }
      >
        <FormProvider {...formMethods}>
          <Stack spacing={2} sx={{ mt: 0 }}>
            {wizardMode && (
              <Stepper activeStep={0} sx={{ mb: 2, mt: 1 }}>
                {WIZARD_STEPS.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}
            <Grid container xs={12}>
              <Typography
                component="div"
                marginLeft={4.5}
                marginBottom={0.5}
                variant="body2"
                sx={{ fontWeight: 700 }}
              >
                Personal Details
              </Typography>
            </Grid>
            <Stack direction="row" gap={2}>
              <Grid item xs={4}>
                <FirstNameField control={control} />
              </Grid>
              <Grid item xs={4}>
                <MiddleNameField control={control} />
              </Grid>
              <Grid item xs={4}>
                <LastNameField control={control} />
              </Grid>
            </Stack>
            <Stack direction="row" gap={2}>
              <Grid item xs={4}>
                <BirthdateField
                  control={control}
                  notProvidedName="birthdate_not_provided"
                  onCheckboxClick={(checked) => {
                    if (!checked) {
                      trigger("birthdate");
                    } else {
                      setValue("birthdate", null, {
                        shouldDirty: true,
                        shouldValidate: true
                      });
                      clearErrors("birthdate");
                    }
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <SexField control={control} required={isSexRequired} />
              </Grid>
              <Grid item xs={4}></Grid>
            </Stack>

            <Grid item xs={12} pb={1}>
              <GenderField control={control} />
            </Grid>

            <Grid item xs={12} pb={1}>
              <ReferralSourceFieldCombo />
            </Grid>

            <Grid item xs={12} pb={1}>
              <Box display="flex" justifyContent="space-between">
                <LocationOnOutlinedIcon sx={{ mt: "30px", mr: 1.5 }} />
                <Grid container width="100%" spacing={4}>
                  <Grid item xs={5}>
                    <ClinicField
                      control={control}
                      name="preferred_clinic"
                      label="Preferred clinic"
                      hideIcon
                    />
                  </Grid>

                  <Grid item xs={7}>
                    <PhysicianField
                      label="Referring Physician"
                      name="referring_physician"
                      control={control}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <DuplicatedPatientWarning
              control={control}
              setValue={setValue}
              onSave={onSave}
            />
            <Grid container width="100%" sx={{ pt: 3 }}>
              <Grid item xs={12} pb={2}>
                <Typography
                  component="div"
                  marginLeft={4.5}
                  marginBottom={0.5}
                  variant="body2"
                  sx={{ fontWeight: 700 }}
                >
                  Contact Information
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <PrimaryContactNotesField control={control} />
              </Grid>
            </Grid>
            <Grid container width="100%">
              <Grid item xs={4}>
                <PhoneField name="phone" control={control} showIcon required />
              </Grid>
              <Grid item xs={8} pl={4}>
                <EmailField control={control} />
              </Grid>
            </Grid>
            <Grid container width="100%">
              <Grid item xs={4}>
                <PhoneTypeField control={control} />
              </Grid>
              <Grid item xs={8} pl={4}>
                <CommunicationMethodField control={control} />
              </Grid>
            </Grid>
            <Grid container width="100%">
              <Grid item xs={4}>
                <WizardTextField
                  control={control}
                  label="Street Address Line 1"
                  name="street_address_1"
                  sx={{ ml: "36px" }}
                />
              </Grid>
              <Grid item xs={8} pl={4}>
                <WizardTextField
                  control={control}
                  label="Street Address Line 2"
                  name="street_address_2"
                  sx={{ ml: "36px" }}
                />
              </Grid>
            </Grid>
            <Grid
              container
              width="100%"
              spacing={2}
              sx={{
                width: "calc(100% - 20px)",
                marginLeft: "20px !important",
                marginTop: "0 !important"
              }}
            >
              <Grid item xs={country !== BAHAMAS_COUNTRY_VALUE ? 3 : 4}>
                <WizardTextField control={control} label="City" name="city" />
              </Grid>
              {country !== BAHAMAS_COUNTRY_VALUE && (
                <Grid item xs={3}>
                  <WizardSelectField
                    control={control}
                    label="State"
                    name="state"
                    options={
                      country === CANADA_COUNTRY_VALUE
                        ? CANADA_PROVINCES.getOptions()
                        : country === US_VIRGIN_ISLANDS_COUNTRY_VALUE
                          ? US_VIRGIN_ISLANDS_TERRITORIES.getOptions()
                          : US_STATES.getOptions()
                    }
                  />
                </Grid>
              )}
              <Grid item xs={country !== BAHAMAS_COUNTRY_VALUE ? 3 : 4}>
                <WizardTextField
                  control={control}
                  label="ZIP Code"
                  name="zip_code"
                />
              </Grid>
              <Grid item xs={country !== BAHAMAS_COUNTRY_VALUE ? 3 : 4}>
                <WizardSelectField
                  control={control}
                  label="Country"
                  name="country"
                  options={COUNTRIES.getOptions()}
                />
              </Grid>
            </Grid>
            <PaymentSourceType control={control} />
            {Number(paymentSourceType) === MANAGED_CARE_PAYMENT_SOURCE && (
              <ManagedCarePlanField
                required
                control={control}
                label="Managed Care Plan"
                name="managed_care_plan"
              />
            )}
            <Grid item xs={12} pb={1}>
              <PatientTagsField
                value={selectedTags}
                onChange={setSelectedTags}
                sx={{ ml: 4.5 }}
              />
            </Grid>
            {hasNoah && (
              <Grid item xs={12} pb={2}>
                <SwitchFormField
                  label="Find / Create Noah Patient"
                  name="connect_to_noah"
                />
              </Grid>
            )}
            <ErrorList
              errors={errors}
              fieldLabels={{ birthdate: "Date of Birth" }}
            />
          </Stack>
        </FormProvider>
      </ModalForm>
    </Grid>
  );
}
